// Game - main game class with fixed-timestep loop and state machine

import { Vec2, CONST } from './utils.js';
import { t } from './i18n.js';
import { Camera } from './camera.js';
import { World } from './world.js';
import { Player } from './player.js';
import { spawnWave, resolveGuardCollisions, planWaveGuards } from './caravan.js';
import { performAttack, performSuper, Projectile, Bomb, Explosion } from './combat.js';
import { spawnLoot } from './loot.js';
import { rollGuardDrop, applyPickup, PickupKind, isGoldPickup } from './pickups.js';
import { UI } from './ui.js';
import { Shop } from './shop.js';
import { Particle, spawnDust, spawnHitSparks, spawnGoldSparkle, spawnDeathBurst, spawnSlash, spawnDashTrail, updateParticles, renderParticles } from './particles.js';
import { GameAudio } from './audio.js';
import { recordRun, getBestScore, getPlayerName, setPlayerName, getActiveRelics, getUnlockedRelics, unlockRelic, isFirstSession, incrementSessionsStarted, getAutoAttack, setAutoAttack, PLAYER_NAME_MAX } from './storage.js';
import { askPlayerName } from './name-entry.js';
import { isIosApp, pseudonymiseLeaderboard } from './leaderboard-names.js';
import { CARDS } from './ui.js';
import { RELIC_DROP_IDS, RELIC_DROP_CHANCE, RELIC_PITY_WAVE, getRelic } from './relics.js';
import { SessionLogger, clearAllSessions, countSessions, uploadAllLocal, fetchLeaderboard, playerSnapshot, countGuardsByType } from './session-logger.js';
import { RebirthCutscene, REBIRTH_DURATION_SEC } from './cutscene/rebirth.js';
import { guardBrainView, runGuardBrain } from './guard-brain.js';

// Game states
export const State = {
  MENU: 'menu',
  PLAYING: 'playing',
  PAUSED: 'paused',
  WAVE_END: 'waveEnd',
  SHOP: 'shop',
  REBIRTH: 'rebirth',
  GAME_OVER: 'gameover',
};

// How long the wave-summary overlay stays up before auto-opening the
// shop. Skippable via click/Enter/Space after WAVE_END_INPUT_LOCK_SEC so
// a held attack key doesn't dismiss it instantly.
export const WAVE_END_DURATION_SEC = 1.8;
const WAVE_END_INPUT_LOCK_SEC = 0.4;

// Live bomb-explosion effects kept at once; the oldest is dropped past this.
const MAX_EXPLOSIONS = 12;

// Grace window after the last enemy dies: coins keep streaming home
// (infinite magnet, see _updatePlaying) and the wave-end transition
// waits for them. Capped so an unreachable straggler can't stall the
// run — at the cap, _endWave auto-credits any remaining loot.
export const WAVE_COIN_SWEEP_MAX_SEC = 8;

// Total wall time the rebirth cutscene plays before the run resets.
// Sourced from the cutscene module so both timing and visuals stay in
// lockstep. Long: this is a once-per-run mythic moment, not a UI prompt.
const REBIRTH_CUTSCENE_SEC = REBIRTH_DURATION_SEC;

// The game-over Telegram invite shows only after a run reaching this wave.
const TELEGRAM_CTA_MIN_WAVE = 12;

export class Game {
  constructor(renderer, input, options = {}) {
    this.renderer = renderer;
    this.input = input;
    this.camera = new Camera(renderer.width, renderer.height);
    this.world = new World();
    this.ui = new UI();

    // Persistent in-world shop. Placed a bit north-east of player spawn so
    // it's visible immediately when the game starts. Off the road vertically.
    this.shop = new Shop(this.world.width / 2 + 150, this.world.height / 2 - 140);
    this.shopOrigin = null; // 'wave' | 'shop' — how we entered the shop state

    // Telemetry logger. Tagged with the current build commit so logs from
    // different code versions can be compared.
    this.build = options.build || { commit: 'unknown', short: 'unknown' };
    this.logger = new SessionLogger({ commit: this.build.commit });
    // Set by the version-check module when a newer build is detected on the
    // server; the menu reads this to render a reload banner. Null = up to date.
    this.updateAvailable = null;
    this._waveStartMs = 0;
    // Seconds spent in the post-clear coin-sweep window. Wave doesn't
    // end until every coin is collected or the sweep timeout fires.
    this._waveClearedSweepSec = 0;

    this.audio = new GameAudio();
    this.rebirthCutscene = new RebirthCutscene();
    this.state = State.MENU;
    this.wave = 0;
    this.score = 0;
    this.caravansRobbed = 0;
    this._dustTimer = 0;
    // Game over waits on the nickname dialog (js/name-entry.js).
    this.askingName = false;

    // Leaderboard cache, populated async from the telemetry server.
    // Refreshed on menu entry; UI reads whatever's here at render time.
    this.leaderboard = [];
    this._leaderboardFetchedAt = 0;
    this._refreshLeaderboard();

    // AI demo mode (set by main.js when ?ai= parameter is present).
    this._aiController = null;
    // Guard brain (set by main.js from ?brain=); null = built-in guard AI.
    this._guardBrain = null;
    // Training polygon (set by main.js from ?polygon=, experiments only):
    // { type, share, fromWave } — that share of each wave's guards is `type`.
    this._polygon = null;
    this._timeScale = 1;

    // Entity lists (populated by later tasks)
    this.player = null;
    this.caravans = [];
    this.guards = [];
    this.loots = [];
    this.pickups = [];
    this.particles = [];
    this.projectiles = [];
    this.bombs = [];
    this.explosions = [];
    this.floatingTexts = [];
    // Total guards spawned for the current wave. Tracked separately because
    // `this.guards` is compacted as they die (see _updatePlaying), so its
    // length is no longer a stand-in for the wave's escort size.
    this._waveGuardTotal = 0;
    this._guardCompactPending = false;

    // Auto-attack: swing on its own whenever something is in melee reach.
    // Toggled with Caps Lock, or by dragging the touch attack button up to
    // the lock target. Persisted like the mute preference.
    this.autoAttack = getAutoAttack();

    // Fixed timestep
    this._accumulator = 0;
    this._lastTime = 0;
    this._running = false;
    this._dt = 1 / 60; // 60 fps fixed step
    this._maxFrameTime = 0.1; // cap to avoid spiral of death

    // Resize handling
    this._onResize = () => {
      this.renderer.resize();
      this.camera.resize(this.renderer.width, this.renderer.height);
    };
    window.addEventListener('resize', this._onResize);
  }

  start() {
    this._running = true;
    this._lastTime = performance.now() / 1000;
    this._tick = this._loop.bind(this);
    requestAnimationFrame(this._tick);
  }

  stop() {
    this._running = false;
  }

  _loop(nowMs) {
    if (!this._running) return;
    requestAnimationFrame(this._tick);

    const now = nowMs / 1000;
    let frameTime = now - this._lastTime;
    this._lastTime = now;

    if (frameTime > this._maxFrameTime) {
      frameTime = this._maxFrameTime;
    }

    this._accumulator += frameTime * this._timeScale * this._relicTimeScale();
    // Cap accumulator so display stutters don't cause a burst of catch-up steps.
    // At x32 speed: 32/60 ≈ 0.53s per frame = ~32 steps. Cap at 40 for headroom.
    const maxAccum = this._dt * 40;
    if (this._accumulator > maxAccum) this._accumulator = maxAccum;

    // Fixed update steps. endFrame() must be tied to update ticks, not RAF —
    // on high-refresh displays (120/144Hz) a RAF may fire with accumulator < dt
    // (zero updates), and clearing keysPressed there would drop one-shot keys
    // (Escape/E/dash) that arrived between ticks before any update saw them.
    while (this._accumulator >= this._dt) {
      this.update(this._dt);
      this.input.endFrame();
      this._accumulator -= this._dt;
    }

    this.render();
  }

  update(dt) {
    // AI controller pre-tick: observe game state, run policy, write input.
    if (this._aiController) {
      this._aiController.tick(this);
    }

    // Global hotkeys (work in any state where they make sense).
    if (this.input.wasPressed('KeyM')) {
      this.audio.toggleMute();
    }

    // Auto-attack toggle. Caps Lock anywhere, or the touch lock gesture
    // (drag off the attack button onto the lock target) during play.
    if (
      this.input.wasPressed('CapsLock') ||
      (this.input.wantsAutoAttackToggle && this.input.wantsAutoAttackToggle())
    ) {
      this.toggleAutoAttack();
    }

    // Touch button rects only matter during PLAYING. Clear in any other
    // state so tapping the bottom-right corner on menus doesn't get
    // swallowed by the dedicated attack/dash slots.
    if (this.state !== State.PLAYING) {
      if (this.input.setAttackButtonRect) this.input.setAttackButtonRect(null);
      if (this.input.setDashButtonRect) this.input.setDashButtonRect(null);
      if (this.input.setAutoLockRect) this.input.setAutoLockRect(null);
    }

    switch (this.state) {
      case State.MENU:
        this._updateMenu(dt);
        break;
      case State.PLAYING:
        // Allow pausing mid-fight.
        if (this.input.wasPressed('Escape') || this.input.wasPressed('KeyP')) {
          this.state = State.PAUSED;
          this.input.endFrame();
          break;
        }
        this._updatePlaying(dt);
        break;
      case State.PAUSED:
        this._updatePaused(dt);
        break;
      case State.WAVE_END:
        this._updateWaveEnd(dt);
        break;
      case State.SHOP:
        this._updateShop(dt);
        break;
      case State.REBIRTH:
        this._updateRebirth(dt);
        break;
      case State.GAME_OVER:
        this._updateGameOver(dt);
        break;
    }
  }

  render() {
    const r = this.renderer;
    r.clear(CONST.COLOR_SAND);

    switch (this.state) {
      case State.MENU:
        this._renderMenu(r);
        break;
      case State.PLAYING:
        this._renderPlaying(r);
        break;
      case State.PAUSED:
        // Render the frozen playing state under a paused overlay.
        this._renderPlaying(r);
        this.ui.renderPaused(r);
        break;
      case State.WAVE_END:
        // Frozen battlefield with the summary panel on top. Suppress
        // touch controls + floating combat texts so the panel sits on a
        // clean backdrop instead of fighting with leftover combat UI.
        this._renderPlaying(r, { skipTouch: true, skipFloating: true });
        this.ui.renderWaveSummary(r, this.waveSummary);
        break;
      case State.SHOP:
        this._renderShop(r);
        break;
      case State.REBIRTH:
        this._renderRebirth(r);
        break;
      case State.GAME_OVER:
        this._renderGameOver(r);
        break;
    }

    // Telegram CTA visibility: only shown on game-over screen. Toggled
    // every frame because every state transition routes through here,
    // saving us from instrumenting each transition site individually.
    this._syncTelegramCta();

    if (this._aiController) {
      this._renderAIOverlay(r);
    }
  }

  // Show the Telegram chat link as a DOM overlay on game-over — only after a
  // run that reached TELEGRAM_CTA_MIN_WAVE (an invite fits a player who got
  // into the game, not one who died on wave 3) — hide it otherwise. Label is
  // set on first sync from the i18n bundle so it matches the player's
  // language without coupling the HTML to lang. Centred under the game-over
  // text, which moves left of the leaderboard on narrow screens.
  get showTelegramCta() {
    return this.state === State.GAME_OVER && this.wave >= TELEGRAM_CTA_MIN_WAVE;
  }

  _syncTelegramCta() {
    const el = (typeof document !== 'undefined')
      ? document.getElementById('tg-cta') : null;
    if (!el) return;
    const cta = t('gameover.communityCta');  // follows the menu's EN/RU switch
    if (el.textContent !== cta) el.textContent = cta;
    const show = this.showTelegramCta ? '1' : '0';
    if (el.dataset.show !== show) el.dataset.show = show;
    const left = this.ui.gameOverTextX != null ? `${Math.round(this.ui.gameOverTextX)}px` : '50%';
    if (el.style.left !== left) el.style.left = left;
  }

  // --- State transitions ---

  startGame() {
    this.audio.init();
    this.audio.startWind();
    this.state = State.PLAYING;
    this.wave = 1;
    this.score = 0;
    // Rebirths in this run = heat level: each one makes guards faster
    // (heatGuardSpeed) and shows as a star next to the wave in the HUD.
    this.rebirths = 0;
    this.caravansRobbed = 0;
    this.caravans = [];
    this.guards = [];
    this.loots = [];
    this.pickups = [];
    this.particles = [];
    this.projectiles = [];
    this.bombs = [];
    this.explosions = [];
    this.floatingTexts = [];
    this.waveDamageTaken = 0; // track damage for flawless bonus
    this.newRecord = false;
    // Snapshot active relics for this run. Re-read on every startGame so
    // mid-run toggles aren't possible (relics are committed at start).
    this.activeRelics = getActiveRelics();
    this._relicToast = null;

    // Reset shop upgrade tracking
    this.ui.reset();

    // Start a fresh telemetry session tagged with the current build.
    // `ai` flags demo-mode runs so the server-side leaderboard can skip
    // them — otherwise SmartPolicy puts itself at the top forever.
    const aiPolicyName = this._aiController && this._aiController.policy
      ? this._aiController.policy.name
      : null;
    this.logger.startSession({
      build: this.build,
      ai: aiPolicyName,
      firstSession: isFirstSession(),
    });

    // Create player at world center
    this.player = new Player(this.world.width / 2, this.world.height / 2);

    // Center camera on player
    this.camera.x = this.player.pos.x - this.renderer.width / 2;
    this.camera.y = this.player.pos.y - this.renderer.height / 2;

    // Novice onboarding (Track A). `isFirstSession()` reads
    // sessionsStarted from storage BEFORE we bump it, so it's true on
    // the very first run only. Apply maxHp + regen as a starting kit
    // so a first-timer has a buffer for the W4 armored introduction
    // without ever picking a card. Telemetry tags the run as
    // `firstSession` so the dashboard can isolate onboarded sessions.
    this._isFirstSession = isFirstSession();
    this._mercyHealUsed = false;
    if (this._isFirstSession) {
      const maxHpCard = CARDS.find(c => c.id === 'maxHp');
      const regenCard = CARDS.find(c => c.id === 'regen');
      if (maxHpCard) maxHpCard.apply(this.player, 1);
      if (regenCard) regenCard.apply(this.player, 1);
      // wave=0 marks "pre-wave" picks so dashboard cohort queries can
      // distinguish starter cards from W1 picks (`mode='starter' AND wave=0`).
      this.logger.logCardPicked('maxHp', 'common', 'starter', 0, 0);
      this.logger.logCardPicked('regen', 'uncommon', 'starter', 0, 0);
      // First two interwave drafts guarantee a sustain card if RNG
      // didn't already offer one — see UI._applyNoviceBias.
      this.ui._noviceBiasRemaining = 2;
    }
    incrementSessionsStarted();

    // Spawn first wave
    this._spawnWave();
  }

  _spawnWave() {
    const newCaravans = spawnWave(this.wave, this.world);
    this.caravans = newCaravans;
    this.guards = [];
    this.projectiles = [];
    this.bombs = [];
    this.waveDamageTaken = 0;
    this._waveStartGold = this.player ? this.player.gold : 0;
    this._waveClearedSweepSec = 0;
    // Head-count budget + elite pressure for this wave. See planWaveGuards:
    // past the cap the surplus arrives as elite tiers rather than bodies.
    const plan = planWaveGuards(this.wave, newCaravans);
    for (let i = 0; i < newCaravans.length; i++) {
      const guards = newCaravans[i].spawnGuards(this.wave, null, {
        count: plan.counts[i],
        pressure: plan.pressure,
        heat: this.rebirths,
        ...(this._polygon && this.wave >= (this._polygon.fromWave || 1) ? { polygon: this._polygon } : {}),
      });
      this.guards.push(...guards);
    }
    this._waveGuardTotal = this.guards.length;
    this._guardCompactPending = false;

    // Novice mercy heal (Track A.2). Full-heal once before wave 4 of a
    // first-ever session — wave 4 is when armored guards debut and the
    // prod data shows 15% of first-time players die there. The heal
    // gives them a clean HP buffer for the introduction.
    // Guarded by `_mercyHealUsed` so a retry-via-rebirth in the same
    // first session doesn't get a second free heal.
    if (
      this._isFirstSession &&
      !this._mercyHealUsed &&
      this.wave === 4 &&
      this.player &&
      this.player.alive
    ) {
      this.player.heal(this.player.maxHp, 'mercy');
      this._mercyHealUsed = true;
      this.addFloatingText(
        this.player.pos.x,
        this.player.pos.y - 24,
        t('onboarding.mercyHeal'),
        '#7fff7f',
        14
      );
    }

    this._waveStartMs = Date.now();
    this._waveActiveMs = 0;
    // Heals before the wave (mercy heal) are already in the HP snapshot
    // below; wave_end reports only in-wave heals.
    if (this.player) this.player.takeHealed();
    const isBoss = this.wave % 5 === 0;
    this.logger.logWaveStart(this.wave, {
      caravans: newCaravans.length,
      guards: this.guards.length,
      boss: isBoss,
      guardsByType: countGuardsByType(this.guards),
      player: playerSnapshot(this.player),
    });
  }

  // Finalise the current wave: sweep up any stragglers (only fires if
  // the coin-sweep grace window hit WAVE_COIN_SWEEP_MAX_SEC before the
  // player picked them up — normally the trigger waits for an empty
  // loots array), apply the flawless bonus, log the wave_end event,
  // build the summary payload and hand off to the WAVE_END overlay
  // state. The shop opens after the overlay timer elapses (or the
  // player skips it).
  _endWave() {
    let pendingGold = 0;
    for (const loot of this.loots) {
      if (loot.alive) {
        pendingGold += loot.value;
        loot.alive = false;
      }
    }
    // Gold pickups still on the field count like coins; buffs and heals
    // are dropped (pointless once the wave is cleared, as in the simulator).
    for (const pickup of this.pickups) {
      if (isGoldPickup(pickup)) pendingGold += applyPickup(pickup.type, this.player, pickup.value).gold;
    }
    if (pendingGold > 0 && this.player) {
      this.player.addGold(pendingGold);
      this.score += pendingGold;
      this.logger.logGoldEarned(pendingGold);
    }
    this.loots = [];
    this.pickups = [];
    // Defuse anything still in the air — a bomb thrown by the last
    // bombardier shouldn't detonate during the wave summary.
    this.bombs = [];

    const anyRobbed = this.caravans.some(c => !c.escaped);
    let flawlessBonus = 0;
    if (this.waveDamageTaken === 0 && anyRobbed) {
      flawlessBonus = CONST.FLAWLESS_WAVE_BONUS * this.wave;
      this.score += flawlessBonus;
      if (this.player) {
        this.player.addGold(flawlessBonus);
        this.logger.logGoldEarned(flawlessBonus);
        this.addFloatingText(
          this.player.pos.x, this.player.pos.y - 40,
          `FLAWLESS! +${flawlessBonus}`, '#00ff88', 20
        );
      }
      this.logger.logFlawless(this.wave, flawlessBonus);
    }

    const escaped = this.caravans.filter(c => c.escaped).length;
    const robbed = this.caravans.filter(c => !c.escaped).length;
    const durationMs = Date.now() - this._waveStartMs;
    this.logger.logWaveEnd(this.wave, {
      durationMs,
      activeMs: Math.round(this._waveActiveMs),
      damageTaken: this.waveDamageTaken,
      caravansRobbed: robbed,
      caravansEscaped: escaped,
      hpEnd: this.player ? this.player.hp : 0,
      healed: this.player ? this.player.takeHealed() : {},
    });

    const goldEarned = this.player ? Math.max(0, this.player.gold - this._waveStartGold) : 0;
    this.waveSummary = {
      wave: this.wave,
      caravansRobbed: robbed,
      caravansEscaped: escaped,
      caravansTotal: this.caravans.length,
      // Every guard the wave spawned; by this point the wave is cleared so
      // the total is the kill count. Reads from the snapshot rather than
      // `this.guards.length`, which shrinks as corpses are compacted out.
      guardsKilled: this._waveGuardTotal,
      damageTaken: this.waveDamageTaken,
      goldEarned,
      flawlessBonus,
      durationMs,
    };
    this._enterWaveEnd();
  }

  _enterWaveEnd() {
    this.state = State.WAVE_END;
    this.waveEndTimer = 0;
    this.input.endFrame();
  }

  _updateWaveEnd(dt) {
    this.waveEndTimer += dt;
    // Held-attack guard: ignore input briefly so the summary doesn't
    // flash-skip on a Space still down from the killing blow.
    const canSkip = this.waveEndTimer >= WAVE_END_INPUT_LOCK_SEC && (
      this.input.wasPressed('Enter') ||
      this.input.wasPressed('Space') ||
      this.input.mouse.clicked
    );
    if (canSkip || this.waveEndTimer >= WAVE_END_DURATION_SEC) {
      this.openShop();
    }
  }

  // Open the shop. `origin = 'wave'` is the auto-triggered between-waves draft
  // (heals on entry), `origin = 'shop'` is the in-world walk-in shop building.
  // Both modes charge gold per pick; they differ in trigger + per-wave allowance.
  openShop(origin = 'interwave') {
    this.state = State.SHOP;
    this.shopOrigin = origin;
    if (origin === 'interwave') {
      this.ui.beginInterwaveDraft(null, this.player);
    } else {
      this.ui.beginShopDraft();
    }
    this.logger.logShopOpened(origin, this.wave);
    this.input.endFrame();
  }

  startNextWave() {
    this.wave++;
    this.state = State.PLAYING;
    // Invalidate the persistent shop offer so a fresh set of cards shows up
    // on the first shop visit of the new wave.
    this.ui.onWaveStart();
    // Respawn the player in the middle of the desert so they don't end up
    // inside the caravan path where the previous wave ended. Stats and HP
    // carry over, but position / motion / dash state are reset.
    if (this.player) {
      this.player.respawnAt(this.world.width / 2, this.world.height / 2);
      // Snap the camera so there's no jarring scroll into the new spawn.
      this.camera.x = this.player.pos.x - this.renderer.width / 2;
      this.camera.y = this.player.pos.y - this.renderer.height / 2;
      this.camera.clampToWorld(this.world.width, this.world.height);
    }
    this._spawnWave();
  }

  // Close the in-world shop and resume the current wave without advancing.
  closeShopToPlaying() {
    this.logger.logShopClosed('shop', this.wave);
    this.state = State.PLAYING;
    this.shopOrigin = null;
    this.input.endFrame();
  }

  // Player took the rebirth sentinel — enter the cutscene state. The actual
  // run reset is deferred until the cutscene timer elapses (see
  // _updateRebirth → _applyRebirthReset) so the player sees the burst land
  // before the world snaps back to wave 1. The telemetry session continues;
  // score persists, but everything else (wave, stats, deck, cards picked)
  // is wiped.
  enterRebirth() {
    this.logger.logRebirth(this.wave, this.score);
    this.rebirthWaveBefore = this.wave;
    this.rebirthTimer = 0;
    this.shopOrigin = null;
    this.state = State.REBIRTH;
    this.rebirthCutscene.reset(this.wave);
    // Subtle initial shake — the cutscene fires a bigger one at the
    // "light" moment via its own cue.
    this.camera.shake(0.4, 0.4);
    this.input.endFrame();
  }

  _updateRebirth(dt) {
    this.rebirthTimer += dt;
    this.rebirthCutscene.update(dt, {
      player: this.player,
      camera: this.camera,
      audio: this.audio,
      screenW: this.renderer.width,
      screenH: this.renderer.height,
    });
    // World-spawned particles (dust kicked up before the cutscene
    // started) still tick so they don't freeze on-screen.
    updateParticles(this.particles, dt);
    this.camera.updateShake(dt);
    // Drain any input collected during the cutscene so it doesn't leak
    // into the freshly-spawned wave 1.
    this.input.endFrame();
    if (this.rebirthCutscene.finished) {
      this._applyRebirthReset();
    }
  }

  // Hard reset of run state at the end of the cutscene. Mirrors startGame()
  // but keeps the telemetry session + accumulated score alive — the player
  // is mid-run, just back at wave 1 with a fresh deck.
  _applyRebirthReset() {
    this.wave = 1;
    this.rebirths++;
    this.caravansRobbed = 0;
    this.caravans = [];
    this.guards = [];
    this.loots = [];
    this.pickups = [];
    this.particles = [];
    this.projectiles = [];
    this.bombs = [];
    this.explosions = [];
    this.floatingTexts = [];
    this.waveDamageTaken = 0;
    this.ui.reset();
    this.player = new Player(this.world.width / 2, this.world.height / 2);
    this.camera.x = this.player.pos.x - this.renderer.width / 2;
    this.camera.y = this.player.pos.y - this.renderer.height / 2;
    this.camera.clampToWorld(this.world.width, this.world.height);

    // Re-apply onboarding to the freshly-respawned Player when the player
    // rebirthed during their very first session. Without this, a
    // first-session rebirth leaves them WEAKER than a fresh first-timer:
    // the new Player has no maxHp/regen, ui.reset() cleared the draft
    // bias counter, and the W4 mercy heal would never fire because
    // _mercyHealUsed stuck at true. We reset _mercyHealUsed so the new
    // post-rebirth W4 still gets the introduction heal — fair, since
    // it's structurally the player's first encounter with armored guards
    // on this player object. `_isFirstSession` is preserved across the
    // rebirth (it was set once in startGame).
    if (this._isFirstSession) {
      const maxHpCard = CARDS.find(c => c.id === 'maxHp');
      const regenCard = CARDS.find(c => c.id === 'regen');
      if (maxHpCard) maxHpCard.apply(this.player, 1);
      if (regenCard) regenCard.apply(this.player, 1);
      this._mercyHealUsed = false;
      this.ui._noviceBiasRemaining = 2;
    }

    this.state = State.PLAYING;
    this._spawnWave();
  }

  _renderRebirth(r) {
    // Keep the world visible under the cutscene overlay so the cutscene
    // reads as something happening *in* the scene, not over a black void.
    // The cutscene drives a camera zoom curve (push-in on dissolve,
    // pull-back on lotus) — apply the same scale to the underlying world
    // render so the overlay glyphs stay anchored to the player.
    const zoom = this.rebirthCutscene.getCameraZoom();
    const zoomed = zoom !== 1;
    if (zoomed) {
      r.save();
      r.translate(r.width / 2, r.height / 2);
      r.scale(zoom, zoom);
      r.translate(-r.width / 2, -r.height / 2);
    }
    this._renderPlaying(r);
    if (zoomed) r.restore();
    this.rebirthCutscene.renderOverlay(r, {
      player: this.player,
      camera: this.camera,
      zoom,
    });
  }

  gameOver() {
    this.state = State.GAME_OVER;
    this.audio.stopWind();
    this.newRecord = recordRun(this.score, this.wave);
    this.logger.logDeath(this.wave, this.score);

    // Ask for a leaderboard name until the player gives one (skipping asks
    // again next death). The upload waits for the answer so the run carries
    // the name; meanwhile the game-over screen can't be left. After that the
    // name auto-attaches via storage. Never for the AI demo.
    if (typeof document !== 'undefined' && !getPlayerName() && !this._aiController) {
      this.askingName = true;
      askPlayerName({
        title: t('gameover.nameTitle'),
        placeholder: t('gameover.namePlaceholder'),
        ok: t('gameover.nameOk'),
        skip: t('gameover.nameSkip'),
        maxLength: PLAYER_NAME_MAX,
      }).catch(() => '').then((entered) => {
        const cleaned = entered ? setPlayerName(entered) : '';
        if (cleaned) this.logger.setPlayerName(cleaned);
        this.askingName = false;
        this._finishGameOver();
      });
      return;
    }
    this._finishGameOver();
  }

  // Upload the finished run and refresh the leaderboard.
  _finishGameOver() {
    this.logger.endSession({
      died: true,
      finalScore: this.score,
      waveReached: this.wave,
    });

    // Refresh leaderboard so the just-uploaded run can appear by the
    // time the player reads the game-over screen. Server processes the
    // POST in <100ms so a short head-start gives it time to land.
    setTimeout(() => this._refreshLeaderboard(true), 800);
  }

  _updatePaused(dt) {
    if (
      this.input.wasPressed('Escape') ||
      this.input.wasPressed('KeyP') ||
      this.input.wasPressed('Space') ||
      this.input.wasPressed('Enter')
    ) {
      this.state = State.PLAYING;
      this.input.endFrame();
    }
  }

  // --- Menu ---

  _updateMenu(dt) {
    // Log export shortcuts — only on the main menu so they don't clash
    // with gameplay input. L downloads everything, Shift+L clears.
    if (this.input.wasPressed('KeyL')) {
      const shift = this.input.keys['ShiftLeft'] || this.input.keys['ShiftRight'];
      console.log(`[menu] KeyL pressed (shift=${!!shift}) — ${shift ? 'clearing sessions' : 'downloading export'}`);
      if (shift) {
        clearAllSessions();
      } else {
        this.logger.downloadExport();
      }
      this.input.endFrame();
      return;
    }

    // U: backfill every session from localStorage to the telemetry
    // server (for sessions collected while offline).
    if (this.input.wasPressed('KeyU')) {
      console.log('[menu] KeyU pressed — starting telemetry backfill');
      uploadAllLocal()
        .then((r) => console.log(`[telemetry] backfill: sent=${r.sent} failed=${r.failed}`))
        .catch((err) => console.error('[telemetry] backfill failed:', err));
      this.input.endFrame();
      return;
    }

    if (this.input.wasPressed('Enter') || this.input.mouse.clicked) {
      this.startGame();
      this.input.endFrame(); // consume input so it doesn't trigger attack on first frame
    }
  }

  _refreshLeaderboard(force = false) {
    const minIntervalMs = 30 * 1000;
    if (!force && Date.now() - this._leaderboardFetchedAt < minIntervalMs) return;
    this._leaderboardFetchedAt = Date.now();
    fetchLeaderboard(10).then((entries) => {
      entries = entries || [];
      // App Store: no other players' nicknames (js/leaderboard-names.js).
      this.leaderboard = isIosApp() ? pseudonymiseLeaderboard(entries, getPlayerName()) : entries;
    });
  }

  _renderMenu(r) {
    // Background - render world as backdrop
    this.world.render(r, this.camera);
    this.ui.renderMenu(r, this);
  }

  // --- Playing ---

  _updatePlaying(dt) {
    // Game time only: excludes pause, shop, wave summary and hidden tabs,
    // unlike the wall-clock durationMs.
    this._waveActiveMs += dt * 1000;
    // Keep Input aware of the on-screen control rects so touchstart can
    // route fingers into the dedicated attack/dash slots.
    if (this.input.setAttackButtonRect) {
      this.input.setAttackButtonRect(this.ui.computeAttackButton(this.renderer));
    }
    if (this.input.setDashButtonRect) {
      this.input.setDashButtonRect(this.ui.computeDashButton(this.renderer));
    }
    if (this.input.setSuperButtonRect) {
      this.input.setSuperButtonRect(this.ui.computeSuperButton(this.renderer));
    }
    // Update player
    if (this.player) {
      // Let input resolve touch-based movement direction relative to the
      // player's current screen position.
      this.input.setPlayerScreen(
        this.player.pos.x - this.camera.x,
        this.player.pos.y - this.camera.y
      );
      // Enter the in-world shop when near it. E is the common convention.
      if (this.input.wasPressed('KeyE') && this.shop.isPlayerNear(this.player.pos)) {
        this.openShop('shop');
        return;
      }
      // Tap on the shop building (mobile-friendly equivalent of E). The
      // tap must land near the shop in world-space and the player must
      // already be in interaction range, matching the E-key contract.
      if (this.input.mouse.clicked && this.shop.isPlayerNear(this.player.pos)) {
        const w = this.camera.screenToWorld(this.input.mouse.x, this.input.mouse.y);
        const dx = w.x - this.shop.pos.x;
        const dy = w.y - this.shop.pos.y;
        if (Math.abs(dx) < 32 && dy > -32 && dy < 20) {
          this.openShop('shop');
          return;
        }
      }

      // Dash input: Shift (either side) or the on-screen dash button.
      // Use current movement vector so the dash goes where the player is
      // actively moving; fall back to facing if standing still.
      const touchDash = this.input.wantsTouchDash && this.input.wantsTouchDash();
      if (this.input.wasPressed('ShiftLeft') || this.input.wasPressed('ShiftRight') || touchDash) {
        const move = this.input.getMovement();
        if (this.player.startDash(move.x, move.y)) {
          this.audio.playDash();
          this.logger.logDash(this.wave);
          spawnDashTrail(
            this.particles,
            this.player.pos.x, this.player.pos.y,
            this.player.dashDir.x, this.player.dashDir.y
          );
        }
      }

      this.player.update(dt, this.input, this.world.width, this.world.height);

      // Route burn DoT damage through telemetry + floating text. Burn
      // bypasses iframes inside player.update; we just observe what was
      // applied this frame and surface it.
      if (this.player.burnDamageThisFrame > 0) {
        const dmg = this.player.burnDamageThisFrame;
        this.waveDamageTaken += dmg;
        this.player.flashTimer = 0.08;
        this.addFloatingText(
          this.player.pos.x, this.player.pos.y - 20,
          `-${dmg}`, '#ff7a2a', 14
        );
        this.logger.logPlayerDamaged(dmg, 'fire-burn', this.wave, this.player.hp);
      }
      if (this.player.burnHorseThisFrame > 0) {
        this._onHorseHit(this.player.burnHorseThisFrame, 'fire-burn');
      }

      // Check for player death
      if (!this.player.alive) {
        this.gameOver();
        return;
      }

      // Continuous dash trail while the dash is active.
      if (this.player.dashTimer > 0) {
        spawnDashTrail(
          this.particles,
          this.player.pos.x, this.player.pos.y,
          this.player.dashDir.x, this.player.dashDir.y
        );
      }

      // Super strike: Q or the on-screen button, once the meter is full.
      const touchSuper = this.input.wantsTouchSuper && this.input.wantsTouchSuper();
      if ((this.input.wasPressed('KeyQ') || touchSuper) && this.player.trySuper()) {
        this._fireSuper();
      }

      // Handle player attack. Two things can pull the trigger without the
      // player pressing anything: the 'reflexes' relic and the auto-attack
      // toggle. Both gate on the same "enemy in melee reach" test and then
      // fall into the existing branch (auto-aim at nearest, swing, damage,
      // lifesteal) unchanged.
      const autoSwing =
        (this.autoAttack || (this.activeRelics && this.activeRelics.has('reflexes')))
        && this.player.attackTimer <= 0
        && this._hasEnemyInMeleeReach();
      if (this.input.wantsAttack() || autoSwing) {
        // Touch button / Space = auto-aim at nearest enemy.
        // Right mouse / tap = aim toward cursor.
        const touchAttack = this.input.wantsTouchAttack && this.input.wantsTouchAttack();
        if (!touchAttack && (this.input.mouse.rightDown || this.input.mouse.clicked)) {
          const world = this.camera.screenToWorld(this.input.mouse.x, this.input.mouse.y);
          const dx = world.x - this.player.pos.x;
          const dy = world.y - this.player.pos.y;
          const len = Math.sqrt(dx * dx + dy * dy);
          if (len > 0.01) {
            this.player.facing = new Vec2(dx / len, dy / len);
          }
        } else {
          // Auto-aim at nearest enemy
          let bestDist = Infinity;
          let bestTarget = null;
          for (const g of this.guards) {
            if (!g.alive) continue;
            const d = this.player.pos.dist(g.pos);
            if (d < bestDist) { bestDist = d; bestTarget = g; }
          }
          for (const c of this.caravans) {
            if (!c.alive) continue;
            const d = this.player.pos.dist(c.pos);
            if (d < bestDist) { bestDist = d; bestTarget = c; }
          }
          if (bestTarget) {
            this.player.face(
              bestTarget.pos.x - this.player.pos.x,
              bestTarget.pos.y - this.player.pos.y
            );
          }
        }
        if (this.player.tryAttack()) {
          this.audio.playAttack();
          this.logger.logAttack();
          spawnSlash(
            this.particles,
            this.player.pos.x,
            this.player.pos.y,
            this.player.facing.x,
            this.player.facing.y,
            this.player.radius + this.player.attackRange,
            this.player.fullArcAttack
          );
          const hits = performAttack(this.player, this.guards, this.caravans);
          // Lifesteal: heal a fraction of total dealt damage this swing.
          if (this.player.lifestealPct > 0 && hits.length > 0) {
            const totalDmg = hits.reduce((s, h) => s + h.damage, 0);
            const heal = Math.floor(totalDmg * this.player.lifestealPct);
            if (heal > 0) {
              this.player.heal(heal, 'lifesteal');
              this.addFloatingText(
                this.player.pos.x, this.player.pos.y - 32,
                `+${heal}`, '#d46a7a', 14
              );
            }
          }
          // Fortify heal (v1.3.0): combat.js already applied it to player.hp;
          // we just surface a floating-text confirmation. Total is attached
          // to `hits` as a non-enumerable property so it doesn't show up in
          // the per-hit damage loop.
          // The heal is fractional (3 × card scale) — show it rounded.
          const fortifyShown = Math.round(hits.fortifyHealTotal || 0);
          if (fortifyShown > 0) {
            this.addFloatingText(
              this.player.pos.x, this.player.pos.y - 44,
              `+${fortifyShown}`, '#7fff7f', 13
            );
          }
          const wasReady = this.player.superReady;
          for (const hit of hits) {
            // Floating damage number
            this.addFloatingText(
              hit.target.pos.x, hit.target.pos.y - 20,
              `-${hit.damage}`, '#fff', 16
            );

            this.logger.logDamageDealt(hit.damage);
            this.player.addSuperCharge(hit.damage);

            // Hit sparks and sound
            spawnHitSparks(this.particles, hit.target.pos.x, hit.target.pos.y);
            this.audio.playHit();
            this.camera.shake(3, 0.1);

            // Flash white on hit
            hit.target.flashTimer = 0.1;

            // Check if guard died
            if (hit.type === 'guard' && !hit.target.alive) {
              this._onGuardKilled(hit.target, 'melee');
            }

            // Check if caravan was destroyed directly
            if (hit.type === 'caravan' && !hit.target.alive) {
              spawnDeathBurst(this.particles, hit.target.pos.x, hit.target.pos.y);
              this._checkCaravanLoot(hit.target);
            }
          }
          if (!wasReady && this.player.superReady) {
            this.audio.playPickup();
            this.addFloatingText(
              this.player.pos.x, this.player.pos.y - 46,
              t('status.superReady'), '#ffb347', 14
            );
          }
        }
      }
    }

    // Update caravans
    for (const caravan of this.caravans) {
      caravan.update(dt, this.player ? this.player.pos : caravan.pos);
    }

    // Update guards
    if (this.player) {
      if (this._guardBrain) {
        runGuardBrain(this._guardBrain, guardBrainView(
          this.player, this.guards, this.caravans, { dt, wave: this.wave }
        ));
      }
      for (const guard of this.guards) {
        guard.update(dt, this.player.pos);

        // Guard attacks player if close enough
        if (guard.canAttack(this.player.pos)) {
          const result = guard.attack(this.player.pos, this.player.vel);
          if (result.bomb) {
            // Bombardier lob: the blast resolves on the fuse, not here.
            this.bombs.push(new Bomb(
              result.origin.x, result.origin.y,
              result.target.x, result.target.y,
              result.damage
            ));
            this.audio.playAttack();
          } else if (result.ranged) {
            // Spawn projectile (regular, fire, or sticky arrow).
            const proj = new Projectile(
              result.origin.x, result.origin.y,
              result.dir.x, result.dir.y,
              result.damage,
              { fire: !!result.fire, sticky: !!result.sticky }
            );
            this.projectiles.push(proj);
          } else {
            // Melee guard swing animation (red tint to distinguish from player)
            const gdir = this.player.pos.sub(guard.pos);
            const glen = gdir.len();
            if (glen > 0.01) {
              spawnSlash(
                this.particles,
                guard.pos.x, guard.pos.y,
                gdir.x / glen, gdir.y / glen,
                guard.radius + guard.attackRange,
                false, '#ff6644', '#ff4422'
              );
            }
            // takeDamage is a no-op during i-frames, so `actual` is 0 when
            // the swing (or a lancer's lunge) was dodged.
            const actual = this.player.takeDamage(result.damage);
            // What landed, counting the share the horse soaked up.
            const landed = actual + this.player.horseAbsorbed;
            this._onHorseHit(this.player.horseAbsorbed, `guard:${guard.type}`);
            // Turn toward the attacker so a counter-attack lands naturally.
            this.player.face(guard.pos.x - this.player.pos.x, guard.pos.y - this.player.pos.y);
            if (landed > 0) {
              this.player.flashTimer = 0.12;
              // A connecting charge hits noticeably harder than a swing —
              // give it the shake to match.
              this.camera.shake(result.charge ? 8 : 4, result.charge ? 0.25 : 0.15);
              this.audio.playPlayerHurt();
              // Knockback only lands with the hit — dodging the lunge
              // dodges the shove too.
              if (result.knockback) {
                this.player.applyKnockback(
                  result.knockback.x, result.knockback.y, result.knockback.force
                );
              }
            }
            if (actual > 0) {
              this.waveDamageTaken += actual;
              this.addFloatingText(
                this.player.pos.x, this.player.pos.y - 20,
                `-${actual}`, CONST.COLOR_HP_BAR, 16
              );
              this.logger.logPlayerDamaged(actual, `guard:${guard.type}`, this.wave, this.player.hp);
            }
            // Thorns: reflect a fraction of melee damage back at the attacker.
            if (this.player.thornsPct > 0 && guard.alive && landed > 0) {
              const reflected = Math.max(1, Math.round(landed * this.player.thornsPct));
              guard.takeDamage(reflected);
              guard.flashTimer = 0.1;
              this.addFloatingText(
                guard.pos.x, guard.pos.y - 20,
                `-${reflected}`, '#6bbf4a', 14
              );
              this.logger.logDamageReflected(reflected);
              if (!guard.alive) {
                this._onGuardKilled(guard, 'thorns');
              }
            }
          }
        }
      }
    }

    // Separate overlapping guards and push them out of caravans so sprites
    // don't stack on the same pixel.
    resolveGuardCollisions(this.guards, this.caravans);

    // Update projectiles
    if (this.player) {
      for (let i = this.projectiles.length - 1; i >= 0; i--) {
        const proj = this.projectiles[i];
        const hit = proj.update(dt, this.player.pos, this.player.radius);
        if (hit) {
          const actual = this.player.takeDamage(proj.damage);
          const landed = actual + this.player.horseAbsorbed;
          this.player.face(-proj.dir.x, -proj.dir.y);
          const source = proj.fire ? 'fire-arrow' : (proj.sticky ? 'sticky-arrow' : 'arrow');
          this._onHorseHit(this.player.horseAbsorbed, source);
          if (landed > 0) {
            this.player.flashTimer = 0.12;
            this.camera.shake(3, 0.12);
            this.audio.playPlayerHurt();
            spawnHitSparks(this.particles, this.player.pos.x, this.player.pos.y);
          }
          if (actual > 0) {
            this.waveDamageTaken += actual;
            this.addFloatingText(
              this.player.pos.x, this.player.pos.y - 20,
              `-${actual}`, CONST.COLOR_HP_BAR, 16
            );
            this.logger.logPlayerDamaged(actual, source, this.wave, this.player.hp);
          }
          // Riders are gated on the hit landing, so i-frames still negate
          // an arrow completely rather than eating the damage but keeping
          // the burn / glue. A hit the horse soaked up still landed.
          if (landed > 0 && proj.fire) {
            this.player.applyBurn(CONST.FIRE_ARROW_BURN_DURATION, CONST.FIRE_ARROW_BURN_DPS);
          }
          if (landed > 0 && proj.sticky) {
            this.player.applySticky(CONST.STICKY_DURATION);
            this.addFloatingText(
              this.player.pos.x, this.player.pos.y - 34,
              t('status.stuck'), '#9acd32', 13
            );
          }
        }
        if (!proj.alive) {
          this.projectiles.splice(i, 1);
        }
      }
    }

    // Update bombs. The blast is a ground field: it ignores i-frames (like
    // the fire-arrow burn) and is telegraphed for the whole fuse, so the
    // counterplay is walking out of the circle rather than dashing.
    for (let i = this.bombs.length - 1; i >= 0; i--) {
      const bomb = this.bombs[i];
      if (bomb.update(dt)) {
        // Only sell the blast if the player can see it. Several
        // bombardiers throwing at once would otherwise shake the camera
        // and fire the explosion sound continuously for detonations
        // happening off-screen.
        const seen = this.camera.isVisible(
          bomb.pos.x - bomb.radius, bomb.pos.y - bomb.radius,
          bomb.radius * 2, bomb.radius * 2
        );
        if (seen) {
          // Visual only (see Explosion). Capped so a late-wave volley of
          // bombs can't pile up effects on a phone.
          if (this.explosions.length >= MAX_EXPLOSIONS) this.explosions.shift();
          this.explosions.push(new Explosion(bomb.pos.x, bomb.pos.y, bomb.radius));
          this.camera.shake(7, 0.25);
          this.audio.playGuardDeath();
        }
        if (this.player && this.player.alive && bomb.hits(this.player.pos, this.player.radius)) {
          const actual = this.player.takeDamage(bomb.damage, { ignoreIframes: true });
          const landed = actual + this.player.horseAbsorbed;
          this._onHorseHit(this.player.horseAbsorbed, 'bomb');
          if (actual > 0) {
            this.waveDamageTaken += actual;
            this.addFloatingText(
              this.player.pos.x, this.player.pos.y - 20,
              `-${actual}`, CONST.COLOR_HP_BAR, 16
            );
            this.logger.logPlayerDamaged(actual, 'bomb', this.wave, this.player.hp);
          }
          if (landed > 0) {
            this.player.flashTimer = 0.15;
            this.audio.playPlayerHurt();
            this.player.applyBurn(CONST.BOMB_BURN_DURATION, CONST.BOMB_BURN_DPS);
          }
        }
      }
      if (!bomb.alive) this.bombs.splice(i, 1);
    }
    if (this.player && !this.player.alive) {
      this.gameOver();
      return;
    }

    // Update loot. Once the wave is cleared (no live enemies, no live
    // caravans), every remaining coin gets an infinite magnet range so
    // it streams home as a fanfare without ever pulling mid-fight. The
    // wave doesn't transition to WAVE_END until the last coin is
    // collected (or the sweep timeout fires) — without this, distant
    // coins would never close on the player. 'miserGrip' relic stacks a
    // 4× speed boost on top so its owner still feels faster.
    if (this.player) {
      const cleared = this._isWaveCleared();
      const miserGrip = cleared
        && this.activeRelics && this.activeRelics.has('miserGrip');
      const magnetMul = cleared ? Infinity : this.player.magnetRangeMul;
      const magnetSpeedMul = miserGrip ? 4 : 1;
      for (let i = this.loots.length - 1; i >= 0; i--) {
        const loot = this.loots[i];
        const collected = loot.update(
          dt, this.player.pos, this.world.width, this.world.height,
          magnetMul, magnetSpeedMul
        );
        if (collected > 0) {
          this.player.addGold(collected);
          this.score += collected;
          this.logger.logGoldEarned(collected);
          spawnGoldSparkle(this.particles, loot.pos.x, loot.pos.y);
          this.audio.playCoin();
          this.addFloatingText(
            loot.pos.x, loot.pos.y - 10,
            `+${collected}`, CONST.COLOR_GOLD, 14
          );
        }
        if (!loot.alive) {
          this.loots.splice(i, 1);
        }
      }

      // Guard-drop pickups ("ништяки"). Gold ones (purse, spice) join the
      // post-clear sweep like coins; buffs/heals keep the normal magnet
      // (pointless to grab once the wave is cleared). heal/buff mutate the
      // player inside applyPickup; coin/jackpot route gold through the
      // same plumbing as coins.
      for (let i = this.pickups.length - 1; i >= 0; i--) {
        const pickup = this.pickups[i];
        const sweep = cleared && isGoldPickup(pickup);
        const collected = pickup.update(
          dt, this.player.pos, this.world.width, this.world.height,
          sweep ? Infinity : this.player.magnetRangeMul, sweep ? magnetSpeedMul : 1
        );
        if (collected) {
          const res = applyPickup(pickup.type, this.player, pickup.value);
          if (res.kind === PickupKind.COIN || res.kind === PickupKind.JACKPOT) {
            this.player.addGold(res.gold);
            this.score += res.gold;
            this.logger.logGoldEarned(res.gold);
          }
          this.logger.logPickup(pickup.type.id, pickup.kind, this.wave);
          this._pickupFeedback(pickup, res);
        }
        if (!pickup.alive) {
          this.pickups.splice(i, 1);
        }
      }
    }

    // Dust particles from moving entities
    this._dustTimer -= dt;
    if (this._dustTimer <= 0) {
      this._dustTimer = 0.08;
      if (this.player && this.player.vel.lenSq() > 400) {
        spawnDust(this.particles, this.player.pos.x, this.player.pos.y, this.player.vel.x, this.player.vel.y);
      }
      for (const guard of this.guards) {
        if (guard.alive && guard.vel.lenSq() > 400) {
          spawnDust(this.particles, guard.pos.x, guard.pos.y, guard.vel.x, guard.vel.y);
        }
      }
    }

    // Update particles
    updateParticles(this.particles, dt);
    for (let i = this.explosions.length - 1; i >= 0; i--) {
      this.explosions[i].update(dt);
      if (!this.explosions[i].alive) this.explosions.splice(i, 1);
    }

    // Wave-end gate. See _tickWaveEndGate — wave only finishes when
    // every coin is collected (or the sweep cap fires).
    if (this.caravans.length > 0 && this._tickWaveEndGate(dt)) {
      this._endWave();
    }

    // Update camera shake
    this.camera.updateShake(dt);

    // Camera follows player if exists
    if (this.player) {
      this.camera.follow(this.player.pos, dt);
      this.camera.clampToWorld(this.world.width, this.world.height);
    }

    // Update floating texts
    for (let i = this.floatingTexts.length - 1; i >= 0; i--) {
      const ft = this.floatingTexts[i];
      ft.y -= 40 * dt;
      ft.life -= dt;
      if (ft.life <= 0) {
        this.floatingTexts.splice(i, 1);
      }
    }

    // Drop corpses from the live list. Deferred to the end of the tick so
    // the update/attack loops above always iterate a stable array.
    if (this._guardCompactPending) {
      this.guards = this.guards.filter(g => g.alive);
      this._guardCompactPending = false;
    }
  }

  // Resolve a super strike the player just spent the meter on: blast FX,
  // damage + shove via performSuper, the usual per-hit bookkeeping. The
  // blast's own damage doesn't refill the meter.
  _fireSuper() {
    const p = this.player;
    if (this.explosions.length >= MAX_EXPLOSIONS) this.explosions.shift();
    this.explosions.push(new Explosion(p.pos.x, p.pos.y, CONST.SUPER_RADIUS));
    this.camera.shake(9, 0.3);
    this.audio.playGuardDeath();
    const hits = performSuper(p, this.guards, this.caravans);
    let kills = 0;
    for (const hit of hits) {
      this.addFloatingText(hit.target.pos.x, hit.target.pos.y - 20, `-${hit.damage}`, '#ffb347', 18);
      this.logger.logDamageDealt(hit.damage);
      hit.target.flashTimer = 0.15;
      spawnHitSparks(this.particles, hit.target.pos.x, hit.target.pos.y);
      if (hit.type === 'guard' && !hit.target.alive) {
        kills++;
        this._onGuardKilled(hit.target, 'super');
      }
      if (hit.type === 'caravan' && !hit.target.alive) {
        spawnDeathBurst(this.particles, hit.target.pos.x, hit.target.pos.y);
        this._checkCaravanLoot(hit.target);
      }
    }
    this.logger.logSuper(this.wave, hits.length, kills);
  }

  // Feedback + telemetry for damage the horse soaked up (`amount` from
  // player.horseAbsorbed / burnHorseThisFrame), and the horse running off
  // once its bar is empty.
  _onHorseHit(amount, source) {
    const p = this.player;
    if (amount > 0) {
      this.addFloatingText(p.pos.x + 14, p.pos.y - 8, `-${amount}`, '#c08a4a', 14);
      this.logger.logHorseDamaged(amount, source, this.wave, p.horse ? p.horse.hp : 0);
    }
    if (p.takeHorseLost()) {
      spawnDeathBurst(this.particles, p.pos.x, p.pos.y + 8);
      this.addFloatingText(p.pos.x, p.pos.y - 46, t('status.horseLost'), '#c08a4a', 14);
      this.logger.logHorseLost(this.wave);
    }
  }

  // Flip auto-attack and persist it. Triggered by Caps Lock or by dragging
  // the touch attack button onto the lock target.
  toggleAutoAttack() {
    this.autoAttack = !this.autoAttack;
    setAutoAttack(this.autoAttack);
    this.audio.playPickup();
    if (this.player) {
      this.addFloatingText(
        this.player.pos.x, this.player.pos.y - 46,
        t(this.autoAttack ? 'status.autoAttackOn' : 'status.autoAttackOff'),
        this.autoAttack ? '#ffd700' : '#aaa', 14
      );
    }
  }

  // Centralized guard-death handling: death FX + sound, kill log, caravan
  // loot check, and a guard-drop ("ништяк") roll. Mirrors the simulator's
  // onGuardKilled so the live game and headless sim share drop behavior.
  // `source` is the kill attribution passed straight to the telemetry log
  // ('melee' | 'thorns' | ...).
  _onGuardKilled(guard, source) {
    // Corpses used to sit in `this.guards` until the wave ended, so every
    // update and render loop kept paying for them. Flag a compaction and
    // run it once at the end of the tick — doing it here would mutate the
    // array the caller is iterating.
    this._guardCompactPending = true;
    spawnDeathBurst(this.particles, guard.pos.x, guard.pos.y);
    this.audio.playGuardDeath();
    this.logger.logGuardKilled(guard.type, this.wave, source, guard.hasFiredFire);
    this._checkCaravanLoot(guard.caravan);
    const drop = rollGuardDrop(guard, this.player, this.wave);
    if (drop) this.pickups.push(drop);
  }

  // Visual + audio feedback for a collected pickup. Floating text shows the
  // gold gained (coin/jackpot), HP restored (heal), or the localized buff
  // name (buff). Purely cosmetic — the effect already applied in applyPickup.
  _pickupFeedback(pickup, res) {
    this.audio.playPickup();
    spawnGoldSparkle(this.particles, pickup.pos.x, pickup.pos.y);
    const x = pickup.pos.x;
    const y = pickup.pos.y - 12;
    if (res.kind === PickupKind.COIN || res.kind === PickupKind.JACKPOT) {
      this.addFloatingText(x, y, `+${res.gold}`, CONST.COLOR_GOLD, 14);
    } else if (res.kind === PickupKind.HEAL) {
      // HP can be fractional (fortify heals), so the restored amount is too.
      this.addFloatingText(x, y, `+${Math.round(res.amount)}`, '#7fff7f', 14);
    } else if (res.kind === PickupKind.BUFF) {
      this.addFloatingText(x, y, t(`${pickup.type.labelKey}.label`), pickup.type.color, 13);
    } else if (res.kind === PickupKind.MOUNT && res.mounted) {
      this.addFloatingText(x, y, t('status.horseMounted'), pickup.type.color, 15);
    }
  }

  // Check if a caravan should drop loot (all its guards dead or caravan itself destroyed)
  _checkCaravanLoot(caravan) {
    if (!caravan || caravan.looted) return;

    // Caravan drops loot if it's dead OR all its guards are dead
    const allGuardsDead = caravan.guards.every(g => !g.alive);
    const shouldDrop = !caravan.alive || allGuardsDead;

    if (shouldDrop) {
      caravan.looted = true;
      if (caravan.alive) {
        caravan.alive = false; // kill the caravan if guards are all dead
      }
      const coins = spawnLoot(caravan);
      this.loots.push(...coins);
      this.caravansRobbed++;
      this.logger.logCaravanRobbed(
        caravan.type,
        caravan.lootValue,
        this.wave,
        !!caravan.isBoss
      );
      if (caravan.isBoss) this._maybeDropRelic();
    }
  }

  // Boss-kill relic drop. Rolls RELIC_DROP_CHANCE against the set of
  // relics the player hasn't unlocked yet. On success, persists the
  // unlock and surfaces an on-screen toast — the player doesn't need to
  // walk over anything, the relic is theirs immediately.
  // Combined multiplier for the two time-warp relics, multiplied into the
  // accumulator alongside `_timeScale` (which stays reserved for AI demo
  // overrides). Only active during PLAYING — menus / shop / cutscenes
  // run at normal pace. Distance is measured from player center to enemy
  // center; thresholds in screen units mirror the typical archer reach.
  _relicTimeScale() {
    if (this.state !== State.PLAYING) return 1;
    if (!this.activeRelics) return 1;
    const slow = this.activeRelics.has('timeSlow');
    const fast = this.activeRelics.has('timeFast');
    if (!slow && !fast) return 1;
    if (!this.player || !this.player.alive) return 1;
    let nearest = Infinity;
    for (const g of this.guards) {
      if (g.alive) {
        const d = this.player.pos.dist(g.pos);
        if (d < nearest) nearest = d;
      }
    }
    for (const c of this.caravans) {
      if (c.alive) {
        const d = this.player.pos.dist(c.pos);
        if (d < nearest) nearest = d;
      }
    }
    const NEAR = 90;
    const FAR = 360;
    if (slow && nearest <= NEAR) return 0.5;
    if (fast && nearest > FAR) return 2.5;
    return 1;
  }

  // Returns true when the wave should transition to WAVE_END THIS tick.
  // Two conditions must both hold:
  //   1. every guard + caravan is gone (the "cleared" check)
  //   2. every loot and gold pickup has been collected, OR the post-clear
  //      coin-sweep grace window has expired (WAVE_COIN_SWEEP_MAX_SEC)
  // The grace timer only advances while cleared, so it can't bleed in
  // from an earlier wave. Factored out of _updatePlaying so unit tests
  // can drive it deterministically.
  _tickWaveEndGate(dt) {
    if (!this._isWaveCleared()) return false;
    this._waveClearedSweepSec += dt;
    const anyLootLeft = this.loots.some(l => l.alive) || this.pickups.some(isGoldPickup);
    return !anyLootLeft || this._waveClearedSweepSec >= WAVE_COIN_SWEEP_MAX_SEC;
  }

  // True when nothing on the field can still hurt the player or carry
  // loot — i.e. the wave is effectively done and the only thing left is
  // sweeping up coins. Used to gate the wave-end transition (waits for
  // the coin sweep) and to flip every coin to infinite-magnet pull.
  _isWaveCleared() {
    if (this.guards) {
      for (const g of this.guards) {
        if (g.alive) return false;
      }
    }
    if (this.caravans) {
      for (const c of this.caravans) {
        if (c.alive) return false;
      }
    }
    return true;
  }

  // True if any alive enemy is currently within the player's melee reach.
  // Used by the 'reflexes' relic to auto-trigger an attack each tick.
  _hasEnemyInMeleeReach() {
    if (!this.player || !this.player.alive) return false;
    const reach = this.player.radius + this.player.attackRange;
    for (const g of this.guards) {
      if (g.alive && this.player.pos.dist(g.pos) <= reach) return true;
    }
    for (const c of this.caravans) {
      if (c.alive && this.player.pos.dist(c.pos) <= reach) return true;
    }
    return false;
  }

  _maybeDropRelic() {
    const unlocked = getUnlockedRelics();
    const undiscovered = RELIC_DROP_IDS.filter(id => !unlocked.has(id));
    if (undiscovered.length === 0) return;
    // Pity rule: a brand-new player (zero owned relics) gets a
    // guaranteed first drop from the wave-RELIC_PITY_WAVE boss. Keeps
    // the meta-progression visible even on a streak of unlucky 8%
    // rolls. After the first unlock the rule disengages — `unlocked`
    // is non-empty and the regular RELIC_DROP_CHANCE gates from there.
    const pity = unlocked.size === 0 && this.wave === RELIC_PITY_WAVE;
    if (!pity && Math.random() >= RELIC_DROP_CHANCE) return;
    const id = undiscovered[Math.floor(Math.random() * undiscovered.length)];
    if (!unlockRelic(id)) return;
    // New unlocks default to active for this run too.
    this.activeRelics.add(id);
    this._relicToast = { id, t: 0, duration: 6 };
    this.logger.logRelicUnlocked(id, this.wave);
  }

  _renderPlaying(r, opts = {}) {
    const { skipTouch = false, skipFloating = false } = opts;
    r.save();
    this.camera.apply(r);

    // World terrain
    // We use the world's own render which draws the cached terrain
    r.restore();
    this.world.render(r, this.camera);
    r.save();
    this.camera.apply(r);

    // In-world shop building. Closed variant when the shop allowance for
    // this wave is already spent — door shut, windows dark, signpost dim.
    const shopClosed = this.ui.shopPickLimitReached();
    this.shop.render(r, shopClosed);

    // Everything below is frustum-culled. Nothing used to be: at late-wave
    // head counts most of the entity list sits far off-screen, and drawing
    // it cost exactly as much as drawing what the player can see.
    // `pad` covers the sprite's own half-extent plus any decoration drawn
    // around it (charge telegraph, blast ring) so nothing pops at the edge.
    const onScreen = (pos, pad) =>
      this.camera.isVisible(pos.x - pad, pos.y - pad, pad * 2, pad * 2);

    // Caravans
    for (const caravan of this.caravans) {
      if (!onScreen(caravan.pos, 48)) continue;
      caravan.render(r);
    }

    // Loot (render under entities)
    for (const loot of this.loots) {
      if (!onScreen(loot.pos, 16)) continue;
      loot.render(r);
    }

    // Guard-drop pickups (render under entities, alongside loot)
    for (const pickup of this.pickups) {
      if (!onScreen(pickup.pos, 20)) continue;
      pickup.render(r);
    }

    // Explosion scorch marks (ground layer)
    for (const ex of this.explosions) {
      if (!onScreen(ex.pos, ex.radius)) continue;
      ex.renderGround(r);
    }

    // Guards. 'engineerEye' relic: attack-range ring + numeric HP under
    // the sprite. Ring drawn BEFORE the sprite (so the sprite stays
    // readable on top) and HP text AFTER. Color hints at the threat type
    // — orange for archers (long range), red for melee (close-up).
    const engineerEye = this.activeRelics && this.activeRelics.has('engineerEye');
    for (const guard of this.guards) {
      // The charge telegraph reaches ahead of the lancer, so a charging
      // guard needs the whole lunge length in its cull padding.
      const pad = guard.chargePhase ? 200 : (engineerEye ? guard.attackRange + 8 : 32);
      if (!onScreen(guard.pos, pad)) continue;
      if (engineerEye && guard.alive) {
        const isArcher = guard.type === 'archer';
        const ringColor = isArcher ? '#ff9944' : '#ff5544';
        r.setAlpha(0.35);
        r.strokeCircle(guard.pos.x, guard.pos.y, guard.attackRange, ringColor, 1);
        r.resetAlpha();
      }
      guard.render(r);
      if (engineerEye && guard.alive) {
        r.text(`${Math.ceil(guard.hp)}/${Math.round(guard.maxHp)}`, guard.pos.x, guard.pos.y + 16, '#ffd0a0', 10, 'center');
      }
    }

    // Projectiles
    for (const proj of this.projectiles) {
      if (!onScreen(proj.pos, 12)) continue;
      proj.render(r);
    }

    // Bombs (marker + incoming arc). Padded generously: the charge itself
    // is drawn along the path from the thrower, which can be off-screen
    // while the landing circle is not.
    for (const bomb of this.bombs) {
      if (!onScreen(bomb.pos, bomb.radius + 220)) continue;
      bomb.render(r);
    }

    // Player (render on top)
    if (this.player) {
      this.player.render(r);
    }

    // Explosions over everything on the ground — the fireball swallows
    // whoever stands in it. Padded for sparks flung past the blast radius.
    for (const ex of this.explosions) {
      if (!onScreen(ex.pos, ex.radius + 110)) continue;
      ex.render(r);
    }

    // Shop interaction prompt (drawn over the player so it's always readable).
    // Hide when shop is closed for the wave — nothing to interact with.
    if (this.player && !shopClosed && this.shop.isPlayerNear(this.player.pos)) {
      this.shop.renderInteractPrompt(r, Boolean(this.ui && this.ui._isTouchDevice));
    }

    // Particles (over entities)
    renderParticles(this.particles, r);

    // Floating texts
    if (!skipFloating) {
      for (const ft of this.floatingTexts) {
        const alpha = Math.max(0, ft.life / ft.maxLife);
        r.setAlpha(alpha);
        r.textOutlined(ft.text, ft.x, ft.y, ft.color, '#000', ft.size || 14, 'center', 'middle');
        r.resetAlpha();
      }
    }

    r.restore();

    // Off-screen caravan indicators — red triangles at the screen edge
    // pointing toward caravans that aren't currently visible.
    this._renderCaravanIndicators(r);

    // 'coinSense' relic: gold arrows for off-screen coins, same layout
    // logic as caravan indicators but with a smaller, tinted triangle.
    if (this.activeRelics && this.activeRelics.has('coinSense')) {
      this._renderLootIndicators(r);
    }

    // 'bloodFog' relic: red vignette overlay when HP drops low. Drawn
    // before the HUD so HP numbers stay readable on top of the fog.
    if (this.activeRelics && this.activeRelics.has('bloodFog')) {
      this._renderBloodFog(r);
    }

    // HUD overlay
    this.ui.renderHUD(r, this);
    // Relic-unlocked celebration toast — drawn on top of HUD so the
    // player can't miss it.
    this._renderRelicToast(r);
    // Touch overlay (attack + dash buttons) — only on touch devices.
    if (!skipTouch) {
      this.ui.renderTouchControls(r, this);
    }
  }

  _renderCaravanIndicators(r) {
    if (!this.caravans || this.caravans.length === 0) return;
    const w = r.width;
    const h = r.height;
    const cx = w / 2;
    const cy = h / 2;
    // Per-edge insets so arrows don't tuck under HUD or touch buttons.
    const padTop = CONST.HUD_HEIGHT + 14;
    const padBottom = 28;
    const padSide = 24;
    const onScreenMargin = 36;

    for (const c of this.caravans) {
      if (!c.alive) continue;
      const sx = c.pos.x - this.camera.x;
      const sy = c.pos.y - this.camera.y;
      // Skip when comfortably on-screen — triangles are only a navigation
      // aid for off-screen targets.
      if (
        sx > onScreenMargin && sx < w - onScreenMargin &&
        sy > onScreenMargin && sy < h - onScreenMargin
      ) {
        continue;
      }

      const dx = sx - cx;
      const dy = sy - cy;
      const len = Math.hypot(dx, dy);
      if (len < 1) continue;
      const nx = dx / len;
      const ny = dy / len;

      // Ray from screen center to caravan, clamped to the inset rectangle.
      const halfW = w / 2 - padSide;
      const halfH = (h - padTop - padBottom) / 2;
      const boxCy = padTop + halfH;
      const tX = nx !== 0 ? halfW / Math.abs(nx) : Infinity;
      const tY = ny !== 0 ? halfH / Math.abs(ny) : Infinity;
      const t = Math.min(tX, tY);
      const ex = cx + nx * t;
      const ey = boxCy + ny * t;

      // Triangle dimensions. Boss caravans get a slightly bigger arrow
      // so the player can spot them at a glance.
      const size = c.isBoss ? 11 : 8;
      const back = size;
      const wing = size * 0.7;
      const tipX = ex + nx * size;
      const tipY = ey + ny * size;
      const px = -ny;
      const py = nx;
      const blX = ex - nx * back + px * wing;
      const blY = ey - ny * back + py * wing;
      const brX = ex - nx * back - px * wing;
      const brY = ey - ny * back - py * wing;

      const color = c.isBoss ? '#ff2222' : '#cc3a14';
      r.setAlpha(0.85);
      r.triangle(tipX, tipY, blX, blY, brX, brY, color);
      r.resetAlpha();
    }
  }

  // 'bloodFog' relic: radial vignette that ramps red from the screen
  // edges toward the center as HP falls below BLOOD_FOG_THRESHOLD.
  // Intensity is quadratic in (threshold − hpFrac) so the warning is
  // subtle at the boundary and unmistakable near zero. A slow pulse
  // (0.85–1.0× peak) keeps the eye drawn to it without strobing.
  _renderBloodFog(r) {
    if (!this.player || this.player.maxHp <= 0) return;
    const hpFrac = Math.max(0, this.player.hp / this.player.maxHp);
    if (hpFrac >= CONST.BLOOD_FOG_THRESHOLD) return;
    const t = 1 - hpFrac / CONST.BLOOD_FOG_THRESHOLD;
    const pulse = 0.85 + 0.15 * Math.sin(performance.now() / 220);
    const alpha = CONST.BLOOD_FOG_PEAK_ALPHA * t * t * pulse;
    const ctx = r.ctx;
    const w = r.width;
    const h = r.height;
    const cx = w / 2;
    const cy = h / 2;
    // Inner radius is where the fog is fully transparent (player still
    // sees the center clearly); outer radius reaches the corners.
    const innerR = Math.max(w, h) * 0.18;
    const outerR = Math.hypot(cx, cy);
    const grad = ctx.createRadialGradient(cx, cy, innerR, cx, cy, outerR);
    grad.addColorStop(0, 'rgba(120, 0, 0, 0)');
    grad.addColorStop(0.65, `rgba(150, 0, 0, ${alpha * 0.55})`);
    grad.addColorStop(1, `rgba(200, 0, 0, ${alpha})`);
    ctx.save();
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);
    ctx.restore();
  }

  // 'coinSense' relic counterpart of _renderCaravanIndicators. Same edge-
  // clamping ray math, smaller gold triangles. Skipped when there are no
  // coins so the function is cheap when the relic is on but the field is
  // empty.
  _renderLootIndicators(r) {
    if (!this.loots || this.loots.length === 0) return;
    const w = r.width;
    const h = r.height;
    const cx = w / 2;
    const cy = h / 2;
    const padTop = CONST.HUD_HEIGHT + 14;
    const padBottom = 28;
    const padSide = 24;
    const onScreenMargin = 24;

    for (const loot of this.loots) {
      if (!loot || !loot.alive) continue;
      const sx = loot.pos.x - this.camera.x;
      const sy = loot.pos.y - this.camera.y;
      if (
        sx > onScreenMargin && sx < w - onScreenMargin &&
        sy > onScreenMargin && sy < h - onScreenMargin
      ) {
        continue;
      }
      const dx = sx - cx;
      const dy = sy - cy;
      const len = Math.hypot(dx, dy);
      if (len < 1) continue;
      const nx = dx / len;
      const ny = dy / len;
      const halfW = w / 2 - padSide;
      const halfH = (h - padTop - padBottom) / 2;
      const boxCy = padTop + halfH;
      const tX = nx !== 0 ? halfW / Math.abs(nx) : Infinity;
      const tY = ny !== 0 ? halfH / Math.abs(ny) : Infinity;
      const t = Math.min(tX, tY);
      const ex = cx + nx * t;
      const ey = boxCy + ny * t;
      const size = 6;
      const back = size;
      const wing = size * 0.7;
      const tipX = ex + nx * size;
      const tipY = ey + ny * size;
      const px = -ny;
      const py = nx;
      const blX = ex - nx * back + px * wing;
      const blY = ey - ny * back + py * wing;
      const brX = ex - nx * back - px * wing;
      const brY = ey - ny * back - py * wing;
      r.setAlpha(0.75);
      r.triangle(tipX, tipY, blX, blY, brX, brY, CONST.COLOR_GOLD);
      r.resetAlpha();
    }
  }

  // Relic-unlocked toast. Renders a centered banner near the top of the
  // screen with the relic icon, label, and a hint that it can be toggled
  // from the main menu. Auto-dismisses when its timer elapses.
  _renderRelicToast(r) {
    const toast = this._relicToast;
    if (!toast) return;
    toast.t += 1 / 60;
    if (toast.t >= toast.duration) { this._relicToast = null; return; }
    const relic = getRelic(toast.id);
    if (!relic) { this._relicToast = null; return; }
    const fade = toast.t < 0.4
      ? toast.t / 0.4
      : (toast.t > toast.duration - 0.6 ? (toast.duration - toast.t) / 0.6 : 1);
    const alpha = Math.max(0, Math.min(1, fade));
    const cx = r.width / 2;
    const top = CONST.HUD_HEIGHT + 30;
    const boxW = Math.min(420, r.width - 40);
    const boxH = 78;
    r.setAlpha(alpha * 0.85);
    r.rect(cx - boxW / 2, top, boxW, boxH, '#1c1410');
    r.strokeRect(cx - boxW / 2, top, boxW, boxH, '#e2b44a', 2);
    r.resetAlpha();
    r.setAlpha(alpha);
    r.textOutlined(t('relic.unlocked.title'), cx, top + 14, '#ffd700', '#000', 14, 'center', 'middle');
    r.textThin(relic.icon, cx - boxW / 2 + 30, top + boxH / 2 + 6, '#fff', 28, 'center', 'middle');
    r.textOutlined(relic.label, cx + 16, top + 38, '#fff', '#000', 16, 'center', 'middle');
    r.text(t('relic.unlocked.hint'), cx, top + boxH - 12, '#aaa', 10, 'center');
    r.resetAlpha();
  }

  // --- Shop / Draft ---

  _closeShop() {
    if (this.shopOrigin === 'shop') {
      this.closeShopToPlaying();
    } else {
      this.startNextWave();
    }
  }

  _updateShop(dt) {
    if (this.input.mouse.clicked && this.player) {
      const mx = this.input.mouse.x;
      const my = this.input.mouse.y;

      // Click on a draft card → apply effect, then close the shop UI. In
      // interwave mode this advances to the next wave; in shop mode it
      // returns the player to the field. One pick per visit either way.
      const idx = this.ui.handleDraftClick(mx, my);
      if (idx >= 0) {
        const card = this.ui.draftOffer[idx] || null;
        const cost = this.ui.getCardCost(card);
        const applied = this.ui.pickCard(idx, this.player);
        if (applied && card) {
          this.logger.logCardPicked(
            card.id,
            card.rarity,
            this.shopOrigin,
            cost,
            this.wave
          );
          if (cost > 0) this.logger.logGoldSpent(cost);
        }
        // Rebirth short-circuits both shop-origin branches — instead of
        // advancing or closing, we hand off to the cutscene state which
        // performs the run reset when it finishes.
        if (applied && this.ui.consumeRebirthPending()) {
          this.enterRebirth();
        } else if (applied && this.shopOrigin === 'interwave') {
          this.startNextWave();
        } else if (applied && this.shopOrigin === 'shop') {
          // Walk-in shop: one purchase per wave. Auto-close so the player
          // gets unambiguous feedback that the allowance is spent — same
          // rhythm as the interwave draft closing on a single pick.
          this.closeShopToPlaying();
        }
        this.input.endFrame();
        return;
      }

      // Reroll offers for gold.
      if (this.ui.isRerollClicked(mx, my)) {
        const rerollCost = this.ui.getRerollCost();
        if (this.ui.tryReroll(this.player)) {
          this.logger.logReroll(
            this.shopOrigin,
            rerollCost,
            this.wave
          );
          this.logger.logGoldSpent(rerollCost);
        }
        this.input.endFrame();
        return;
      }

      // Close / skip button.
      if (this.ui.isSkipClicked(mx, my)) {
        this._closeShop();
        return;
      }
    }

    // Enter = skip / close.
    if (this.input.wasPressed('Enter')) {
      this._closeShop();
      return;
    }

    // In world-shop mode Esc/E exits without advancing the wave.
    if (this.shopOrigin === 'shop' && (
      this.input.wasPressed('Escape') || this.input.wasPressed('KeyE')
    )) {
      this._closeShop();
      return;
    }
  }

  _renderShop(r) {
    this.ui.renderShop(r, this.player, this);
  }

  // --- Game Over ---

  _updateGameOver(dt) {
    if (this.askingName) return;
    // Space is excluded — player is likely still holding it from combat
    // and would skip the screen without reading it.
    if (this.input.wasPressed('Enter') || this.input.mouse.clicked) {
      this.state = State.MENU;
      this._refreshLeaderboard(); // pull fresh standings, including the just-uploaded run
      this.input.endFrame(); // consume input so it doesn't immediately start a new game
    }
  }

  _renderGameOver(r) {
    this.ui.renderGameOver(r, this);
  }

  _renderAIOverlay(r) {
    const name = this._aiController.policy.name;
    const speed = this._timeScale;
    r.setAlpha(0.8);
    r.textOutlined(
      `AI: ${name}  x${speed}`,
      r.width - 10, r.height - 20,
      '#00ff88', '#000', 14, 'right', 'middle'
    );
    r.text(
      t('ai.hints'),
      r.width - 10, r.height - 40,
      '#00aa55', 11, 'right'
    );
    r.resetAlpha();
  }

  // --- Helpers ---

  addFloatingText(x, y, text, color = '#fff', size = 14) {
    this.floatingTexts.push({
      x, y, text, color, size,
      life: 1.0,
      maxLife: 1.0,
    });
  }

  destroy() {
    window.removeEventListener('resize', this._onResize);
    this.input.destroy();
    this.audio.stopWind();
    this.stop();
  }
}
